// Unified counterparty typeahead picker — replaces the raw "paste a UUID"
// input in the Add Party modal (the follow-up the PR-3 helper copy
// promised). Type-scoped: searches the selected counterparty type's own
// module list endpoint (the vendor assign modal's registry search is the
// in-module precedent), debounced at ≥2 characters, keyboard navigable.
// Selection fills the id invisibly and renders as a chip with a clear
// affordance. Types without a search seam (LocalEnforcer / OtherContact)
// fall back to the manual id input.

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { Input, Label } from "@/design-system/components/ui";
import {
  businessesApi,
  entitiesApi,
  personsApi,
} from "@/api/counterparties";
import { brokersApi } from "@/api/brokers";
import { userSearchApi } from "@/api/workTasks";
import { vendorsApi } from "@/api/portfolioAdmin/client";
import { displayEmail } from "@/design-system/lib/format";
import { brokerPickerLabel } from "@/lib/brokerDisplay";

export interface SelectedCounterparty {
  id: string;
  name: string;
  detail?: string | null;
  /** The searcher's raw record, for a host that needs more than the label
   *  (the Referral Disclosure preview needs the broker's name fields). */
  data?: unknown;
}

interface PickerResult {
  id: string;
  name: string;
  email: string | null;
  detail: string | null;
  /** Shown in place of the email on the option row and the chip, when set. */
  caption?: string | null;
  data?: unknown;
}

type SearchFn = (q: string) => Promise<PickerResult[]>;

const MAX_RESULTS = 20;

/** Per-type search adapters over each module's existing list/search
 *  endpoint — no new backend surface. */
const SEARCHERS: Record<string, SearchFn> = {
  Person: async (q) => {
    const page = await personsApi.list({ q, pageSize: MAX_RESULTS });
    return page.data.map((p) => ({
      id: p.id,
      name: p.displayName,
      email: displayEmail(p.primaryEmail),
      detail: p.state ?? null,
    }));
  },
  LegalEntity: async (q) => {
    const page = await entitiesApi.list({ q, pageSize: MAX_RESULTS });
    return page.data.map((e) => ({
      id: e.id,
      name: e.dba ? `${e.legalEntityName} (${e.dba})` : e.legalEntityName,
      email: null,
      detail: e.entityType,
    }));
  },
  Business: async (q) => {
    const page = await businessesApi.list({ q, pageSize: MAX_RESULTS });
    return page.data.map((b) => ({
      id: b.id,
      name: b.dba ? `${b.legalEntityName} (${b.dba})` : b.legalEntityName,
      email: null,
      detail: b.hqState ?? b.naicsCode ?? null,
    }));
  },
  Broker: async (q) => {
    const page = await brokersApi.list({ q, pageSize: MAX_RESULTS, view: "All" });
    return page.data.map((b) => {
      const { primary, secondary } = brokerPickerLabel(b);
      return {
        id: b.id,
        name: primary,
        email: b.email ?? null,
        detail: secondary || b.status,
      };
    });
  },
  /** The cover letter's referring broker (D7): every pipeline stage and no
   *  archived broker (`view: "Pipeline"`), matching firm, DBA, contact and
   *  email server-side; each row shows the firm, the contact and the stage.
   *  `data` is the full `BrokerSummaryDto`. */
  ReferringBroker: async (q) => {
    const page = await brokersApi.list({ q, pageSize: MAX_RESULTS, view: "Pipeline" });
    return page.data.map((b) => ({
      id: b.id,
      name: brokerPickerLabel(b).primary,
      email: b.email ?? null,
      detail: b.status,
      caption: [(b.name ?? "").trim(), b.status].filter((x) => x.length > 0).join(" · "),
      data: b,
    }));
  },
  Vendor: async (q) => {
    const rows = await vendorsApi.search({ name: q });
    return rows.slice(0, MAX_RESULTS).map((v) => ({
      id: v.id,
      name: v.name,
      email: displayEmail(v.email),
      detail: v.vendorType,
    }));
  },
  PhoenixUser: async (q) => {
    const rows = await userSearchApi.search(q, MAX_RESULTS);
    return rows.map((u) => ({
      id: u.id,
      name: u.displayName,
      email: u.email,
      detail: null,
    }));
  },
};

export function hasCounterpartySearch(counterpartyType: string): boolean {
  return counterpartyType in SEARCHERS;
}

function useDebounced(value: string, ms = 250): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

interface CounterpartyPickerProps {
  counterpartyType: string;
  value: SelectedCounterparty | null;
  onChange: (value: SelectedCounterparty | null) => void;
  /** Field label. Defaults to "Counterparty" (the Add Party modal's wording);
   *  reusing surfaces that pick a specific role pass their own. */
  label?: string;
  /** The thing being searched, for the placeholder and the empty line.
   *  Defaults to `counterpartyType`. */
  noun?: string;
}

export function CounterpartyPicker({
  counterpartyType,
  value,
  onChange,
  label = "Counterparty",
  noun = counterpartyType,
}: CounterpartyPickerProps) {
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounced = useDebounced(search);
  const enabled =
    hasCounterpartySearch(counterpartyType) && debounced.trim().length >= 2;

  const results = useQuery<PickerResult[]>({
    queryKey: ["counterparty-search", counterpartyType, debounced.trim()],
    queryFn: () => SEARCHERS[counterpartyType](debounced.trim()),
    enabled,
  });
  const options = useMemo(() => results.data ?? [], [results.data]);

  useEffect(() => setActiveIndex(-1), [debounced, counterpartyType]);

  const select = (r: PickerResult) => {
    onChange({ id: r.id, name: r.name, detail: r.caption ?? r.email ?? r.detail, data: r.data });
    setSearch("");
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // A search typeahead must never submit its enclosing <form>. Enter is
    // contained UNCONDITIONALLY — ahead of the no-options early return, and
    // whether or not an option is highlighted (activeIndex starts at -1, so
    // "nothing highlighted" is the common path). Letting it bubble triggers
    // implicit form submission, which on the partner-brand edit page silently
    // saves the record with a half-cleared owner.
    if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < options.length) {
        select(options[activeIndex]);
      }
      return;
    }
    if (options.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % options.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? options.length - 1 : i - 1));
    } else if (e.key === "Escape") {
      setSearch("");
    }
  };

  if (value) {
    return (
      <div>
        <Label>{label}</Label>
        <span
          className="inline-flex items-center gap-space-2 rounded-full border border-border-light bg-white-warm px-space-2 py-space-1 text-small text-onyx"
          data-testid="counterparty-chip"
        >
          <span className="font-semibold">{value.name}</span>
          {value.detail && (
            <span className="text-caption text-gray-mid">{value.detail}</span>
          )}
          <button
            type="button"
            onClick={() => {
              onChange(null);
              // Return focus to the search input for "change" flows.
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
            aria-label="Clear selected counterparty"
            className="text-gray-mid hover:text-onyx"
            data-testid="counterparty-chip-clear"
          >
            <X size={12} aria-hidden="true" />
          </button>
        </span>
      </div>
    );
  }

  return (
    <div>
      <Label htmlFor="counterparty-search">{label}</Label>
      <div className="relative">
        <Search
          size={14}
          className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid"
          aria-hidden="true"
        />
        <Input
          id="counterparty-search"
          ref={inputRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={`Search ${noun} records by name…`}
          className="pl-[28px]"
          role="combobox"
          aria-expanded={enabled && options.length > 0}
          aria-controls="counterparty-search-listbox"
          aria-activedescendant={
            activeIndex >= 0 ? `counterparty-option-${activeIndex}` : undefined
          }
          autoComplete="off"
          data-testid="counterparty-search-input"
        />
      </div>
      {search.trim().length > 0 && search.trim().length < 2 && (
        <p className="text-caption text-gray-mid mt-1 m-0">
          Type at least 2 characters to search.
        </p>
      )}
      {enabled && (
        <ul
          id="counterparty-search-listbox"
          role="listbox"
          aria-label={`${noun} search results`}
          className="m-0 mt-space-1 p-0 list-none max-h-56 overflow-y-auto rounded-md border border-border-light bg-white-pure"
          data-testid="counterparty-search-results"
        >
          {results.isLoading && (
            <li className="px-space-2 py-space-1 text-small text-gray-mid italic">
              Searching…
            </li>
          )}
          {results.isError && (
            <li className="px-space-2 py-space-1 text-small text-status-error">
              Search failed. Try again.
            </li>
          )}
          {!results.isLoading && !results.isError && options.length === 0 && (
            <li className="px-space-2 py-space-1 text-small text-gray-mid">
              No {noun} records match.
            </li>
          )}
          {options.map((r, i) => (
            <li key={r.id} role="presentation">
              <button
                type="button"
                id={`counterparty-option-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                onClick={() => select(r)}
                onMouseEnter={() => setActiveIndex(i)}
                className={`w-full flex items-baseline gap-space-2 px-space-2 py-space-1 text-left ${
                  i === activeIndex ? "bg-white-warm" : "bg-white-pure"
                }`}
                data-testid={`counterparty-option-${r.id}`}
              >
                <span className="text-small text-onyx font-semibold truncate">
                  {r.name}
                </span>
                {(r.caption ?? r.email) && (
                  <span className="text-caption text-gray-mid truncate">{r.caption ?? r.email}</span>
                )}
                <span className="text-caption text-gray-mid tabular-nums ml-auto shrink-0">
                  {r.id.slice(0, 8)}…
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
